
import classNames from "classnames";
import React from "react";
import { BibleSearchSetting } from "../../../entities/BibleSearchSettings";
import { BuilderPreferences } from "../../../entities/BuilderPreferences";
import { DrawerContent } from "../../../entities/DrawerContent";
import { SermonGeniusSearchItem } from "../../../services/GeniusSearchService";
import DateHelper from "../../../utilities/DateHelper";
import { EventTracking } from "../../../utilities/Tracking/EventTracking";
import { useEditor } from "../editor/useEditor";
import { useBuilder } from "../useBuilder";
import { BibleSearch } from "./bibleSearch";
import { BibleSettingsDialog } from "./components/bibleSettingsDialog";
import { DetailsContent } from "./detailsContent";
import { GeniusSearch } from "./geniusSearch";
import {IllustrationSearch} from "./illustrationSearch";
import {SermonSearch} from "./sermonSearch";
import { SlidesContent } from "./slidesContent";
import { SermonAnalyzer } from "./sermonAnalyzer";
import { useBuilderDrawer } from "./useBuilderDrawer";
import { Drawer, makeStyles } from "@material-ui/core";
import { useIsMobile } from "../../../components/Mui3WidthProvider";
import { Assistant } from "./assistant";

const drawerWidth = 350;

const useStyles = makeStyles((theme) => ({
	toolbar: theme.mixins.toolbar,
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
		[theme.breakpoints.down("sm")]: {
			width: "90%",
		},
	},
	drawerOpen: {
		[theme.breakpoints.up("md")]: {
			width: drawerWidth,
			transition: theme.transitions.create("width", {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.enteringScreen,
			}),
		},
		[theme.breakpoints.down("sm")]: {
			width: "90%",
		},
	},
	drawerClose: {
		[theme.breakpoints.up("md")]: {
			width: drawerWidth,
			transition: theme.transitions.create("width", {
				easing: theme.transitions.easing.sharp,
				duration: theme.transitions.duration.leavingScreen,
			}),
			overflowX: "hidden",
		},
	},
}));

interface Props {
	open: boolean;
	onDrawerToggle: (open: boolean, contentType?: DrawerContent) => void;
	contentType?: DrawerContent;
}

export function MainDrawer(props: Props) {
	const { open: propsOpen } = props;
	const editor = useEditor();
	const builder = useBuilder();
	const builderDrawer = useBuilderDrawer();
	const { drawerContent } = builderDrawer;
	const classes = useStyles();
	const isMobile = useIsMobile();

	const [lastSearch, setLastSearch] = React.useState("");
	const [presetSearchText, setPresetSearchText] = React.useState<string | undefined>();
	const [geniusSearchCounter, setGeniusSearchCounter] = React.useState<Date | undefined>();
	const [translationId, setTranslationId] = React.useState<string | undefined>();
	
	const [bibleVerseSearchResult, setBibleVerseSearchResult] = React.useState<SermonGeniusSearchItem[]>([]);
	const [illustrationSearchResult, setIllustrationSearchResult] = React.useState<SermonGeniusSearchItem[]>([]);
	const [sermonSearchResult, setSermonSearchResult] = React.useState<SermonGeniusSearchItem[]>([]);

	const [settingsDialogOpen, setSettingsDialogOpen] = React.useState(false);
	const [open, setOpen] = React.useState(false);

	React.useEffect(() => {
		setInterval(() => {
			if (geniusSearchCounter) {
				setLastSearch(`Updated < ${DateHelper.timeSinceDate(geniusSearchCounter)}`);
			}
		}, 1000);
	}, [geniusSearchCounter, setLastSearch]);

	// Allow parent changing open on props to update local open state
	React.useEffect(() => {
		setOpen(propsOpen);
	}, [propsOpen]);

	// When drawer content is opened, the editor format bubble needs to be hidden since it's calculated postion will be wrong
	React.useEffect(() => {
		if (open) {
			editor.hideFormatBubble();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const updateGeniusResults = (
		verses: SermonGeniusSearchItem[],
		illustrations: SermonGeniusSearchItem[],
		sermons: SermonGeniusSearchItem[],
		translationId: string
	) => {
		const updatedSearchDate = new Date();
		setBibleVerseSearchResult(verses);
		setIllustrationSearchResult(illustrations);
		setSermonSearchResult(sermons);
		setGeniusSearchCounter(updatedSearchDate);
		setLastSearch(`Updated < ${DateHelper.timeSinceDate(updatedSearchDate)}`);
		setTranslationId(translationId);
	};

	const removeSermonResult = (id: string) => {
		const updatedSermons = sermonSearchResult.filter((s) => s.id !== id);
		setSermonSearchResult(updatedSermons);
	};

	const removeIllustrationResult = (id: string) => {
		const updatedIllustrations = illustrationSearchResult.filter((i) => i.id !== id);
		setIllustrationSearchResult(updatedIllustrations);
	};

	const saveExploreSettings = (selectedTranslationId: string, selectedCopySetting: BibleSearchSetting, selectedViewSetting: BibleSearchSetting) => {
		setSettingsDialogOpen(false);

		let preferences: BuilderPreferences = {
			...builder.preferences,
			bibleTranslationId: selectedTranslationId,
			bibleSearchCopySetting: selectedCopySetting,
			bibleSearchViewSetting: selectedViewSetting,
		};
		builder.savePreferences(preferences);
	};

	const onDrawerToggle = (open: boolean) => {
		props.onDrawerToggle(open);
		if (!open) {
			EventTracking.Builder.onCloseMainDrawerFromX();
		}
	};

	const onRelatedSearch = (type: DrawerContent, queryText: string) => {
		if (type === DrawerContent.Illustration || type === DrawerContent.Sermon) {
			setPresetSearchText(queryText);
			// This used to be in a setState callback before the hooks refactor
			props.onDrawerToggle(true, type);
		}
	};

	return (
		<>
			<Drawer
				open={open}
				variant={isMobile ? "temporary" : "persistent"}
				className={classNames(classes.drawer, {
					[classes.drawerOpen]: open,
					[classes.drawerClose]: !open,
				})}
				classes={{
					paper: classNames({
						[classes.drawerOpen]: open,
						[classes.drawerClose]: !open,
					}),
				}}
				anchor="right"
			>
				{drawerContent === DrawerContent.Analyzer && <SermonAnalyzer onDrawerToggle={onDrawerToggle} /> }
				{drawerContent === DrawerContent.Slides && <SlidesContent />}
				{drawerContent === DrawerContent.Assistant && <Assistant />}
				{drawerContent === DrawerContent.Genius && 
					<GeniusSearch
						asset={builder.builderAsset}
						lastSearched={lastSearch}
						onDrawerToggle={onDrawerToggle}
						geniusSearchResults={updateGeniusResults}
						geniusSearchCounter={geniusSearchCounter}
						removeSermonResult={removeSermonResult}
						removeIllustrationResult={removeIllustrationResult}
						onCopyVerse={(verseContent) => builder.copyToClipboard(verseContent, "Verse")}
						onBibleVerseSelected={(selectedScriptureReference) =>
							builder.handleContentDetailSelection({ type: "Bible Verse", selectedScriptureReference })
						}
						preferences={builder.preferences}
						onIllustrationSelected={(selectedIllustrationId) =>
							builder.handleContentDetailSelection({ type: "Illustration", selectedIllustrationId })
						}
						onCopyIllustration={(illustrationContent) => builder.copyToClipboard(illustrationContent, "Illustration")}
						onSermonSelected={(selectedSermonId) => builder.handleContentDetailSelection({ type: "Sermon", selectedSermonId })}
						onCopySermon={(sermonContent) => builder.copyToClipboard(sermonContent, "Sermon")}
						verses={bibleVerseSearchResult}
						illustrations={illustrationSearchResult}
						sermons={sermonSearchResult}
						onError={builder.setAlert}
						translationId={translationId}
					/>
				}
				{drawerContent === DrawerContent.Details &&	
					<DetailsContent 
						onDrawerToggle={onDrawerToggle} 
						asset={builder.builderAsset} 
					/>
				}
				{drawerContent === DrawerContent.Illustration &&
					<IllustrationSearch
						presetSearchText={presetSearchText}
						onDrawerToggle={onDrawerToggle}
						onIllustrationSelected={(selectedIllustrationId) =>
							builder.handleContentDetailSelection({ type: "Illustration", selectedIllustrationId })
						}
						onCopyIllustration={(illustrationContent) => builder.copyToClipboard(illustrationContent, "Illustration")}
					/>
				}
				{drawerContent === DrawerContent.Sermon &&
					<SermonSearch
						presetSearchText={presetSearchText}
						onDrawerToggle={onDrawerToggle}
						onSermonSelected={(selectedSermonId) => builder.handleContentDetailSelection({ type: "Sermon", selectedSermonId })}
						onCopySermon={(sermonContent) => builder.copyToClipboard(sermonContent, "Sermon")}
					/>
				}
				{drawerContent === DrawerContent.Bible &&
					<BibleSearch
						onPreferenceChange={builder.savePreferences}
						onRelatedSearch={onRelatedSearch}
						onDrawerToggle={onDrawerToggle}
						onCopyVerse={(verseContent) => builder.copyToClipboard(verseContent, "Verse")}
						onBibleVerseSelected={(selectedScriptureReference) =>
							builder.handleContentDetailSelection({ type: "Bible Verse", selectedScriptureReference })
						}
						preferences={builder.preferences}
						onSettingsDialogToggle={() => {
							setSettingsDialogOpen(!settingsDialogOpen);
							if (!settingsDialogOpen) {
								EventTracking.Builder.onOpenSettings("Bible Drawer Interaction");
							}
						}}
					/>
				}
			</Drawer>
			<BibleSettingsDialog
				key={builder.preferences.bibleTranslationId}
				open={settingsDialogOpen}
				onClose={() => setSettingsDialogOpen(false)}
				selectedTranslationId={builder.preferences.bibleTranslationId!}
				selectedCopySetting={builder.preferences.bibleSearchCopySetting!}
				selectedViewSetting={builder.preferences.bibleSearchViewSetting!}
				onSaveSettings={saveExploreSettings}
			/>
		</>
	);
}